import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { CheckCircle } from "lucide-react";

const completionSchema = z.object({
  notes: z.string().max(1000, "Notes must be under 1000 characters").optional(),
});

type CompletionFormData = z.infer<typeof completionSchema>;

interface SessionCompletionModalProps {
  isOpen: boolean;
  onClose: () => void;
  sessionId: string;
  studentName: string;
  onComplete: () => void;
}

export default function SessionCompletionModal({
  isOpen,
  onClose,
  sessionId,
  studentName,
  onComplete,
}: SessionCompletionModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  
  const form = useForm<CompletionFormData>({
    resolver: zodResolver(completionSchema),
    defaultValues: {
      notes: "",
    },
  });

  const handleSubmit = async (data: CompletionFormData) => {
    setIsSubmitting(true);
    try {
      const { error } = await supabase.functions.invoke('update-session-status', {
        body: {
          sessionId,
          status: 'completed',
          duration_minutes: 60,
          completion_notes: data.notes,
        }
      });

      if (error) throw error;

      toast({
        title: "Session Completed",
        description: "Session has been marked as completed with your notes.",
      });

      onComplete();
      onClose();
    } catch (error: any) {
      toast({
        title: "Error",
        description: "Failed to complete session. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-600" />
            Complete Session
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            You're completing the session with <strong>{studentName}</strong>. 
            Add any notes about what was covered or progress made.
          </p>
          
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="notes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Session Notes (Optional)</FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        placeholder="What did you cover in this session? Any key takeaways or progress notes..."
                        className="resize-none"
                        rows={4}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <div className="flex justify-end gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onClose}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Completing..." : "Complete Session"}
                </Button>
              </div>
            </form>
          </Form>
        </div>
      </DialogContent>
    </Dialog>
  );
}